import * as React from "react";
import { applyOrderStd, CtrlColumns } from "src/components/tables/TableStore";
import { Checkbox } from "antd";
import { observer } from "mobx-react-lite";
import { DragableIconStd } from "src/common/icons";
import { SortableList, SortableItemDef } from "src/components/SortableList";
import { classNames } from "src/common/classNames";
import styles from "./ColumnSettingsList.module.less";

interface PropsColumnSettingsList {
  store: CtrlColumns;
  freezeLine: boolean;
}

const freezeKey = "~fixedLeft";

export const ColumnSettingsList: React.FC<PropsColumnSettingsList> = observer(
  ({ store, freezeLine }) => {
    const canDragTotal = !!store.setColumnOrder;
    const sortedColumns = applyOrderStd(store, store.columns);
    const displayedColumns = sortedColumns
      .filter(({ condition }) => !condition || condition())
      .filter(
        ({ title, visibility }) =>
          !(
            visibility === "alwaysVisible" &&
            typeof title === "string" &&
            title.trim() === ""
          ),
      );

    const items: SortableItemDef[] = displayedColumns.map((col) => {
      const canDrag = canDragTotal && !col.disabledDrag;
      return {
        key: col.key,
        noDraggable: !canDrag || !!col.disabledDrag,
        content: (
          <label
            key={col.key}
            className={classNames([
              styles.columnInfo,
              [!canDrag, styles.disabled],
            ])}
          >
            <div className={styles.checkBoxWrapper}>
              <Checkbox
                checked={store.isColumnVisible(col.key)}
                onChange={(e) =>
                  store.setColumnVisible(col.key, e.target.checked)
                }
                disabled={!store.canColumnHide(col.key)}
              />
              <span>{col.title}</span>
            </div>
            {canDrag && <DragableIconStd />}
          </label>
        ),
      };
    });

    if (freezeLine) {
      const key = freezeKey;
      const freezePos = store.fixedLeftRaw;
      items.splice(freezePos, 0, {
        key,
        content: (
          <label key={key} className={styles.freezeContainer}>
            <div className={styles.freezeLabel}>Freeze Line</div>
            <div className={styles.freezeLine} />
            <DragableIconStd />
          </label>
        ),
      });
    }

    const handleSortableChange = (newItems: SortableItemDef[]) => {
      const keys = newItems.map((item) => String(item.key));
      const freezePos = keys.findIndex((key) => key === freezeKey);
      if (freezePos >= 0) {
        keys.splice(freezePos, 1);
      }
      store.setColumnOrder?.(keys);
      if (freezePos >= 0) {
        store.setFixedLeftRaw(freezePos);
      }
    };

    return (
      <SortableList
        containerClassName={styles.container}
        items={items}
        onChange={handleSortableChange}
      />
    );
  },
);
